import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import TechnicianPicker from "./TechnicianPicker";
import { useStartTechnicalWorkMutation } from "../technicalApi";

const today = () => new Date().toISOString().slice(0, 10);

// Start opens the work and assigns technicians in one step - the API requires
// at least one technician ("At least one technician must be assigned to start
// this work"), so the picker is mandatory here rather than optional.
//
// In practice this is the Installation path: a Complaint or Service Visit
// reaches ASSIGNED the moment a technician is put on it and never needs a
// separate Start. The button is still offered for those where the status
// allows it, since the server permits it.
const StartWorkModal = ({ show, onClose, work, technicians = [] }) => {
  const [startWork, { isLoading }] = useStartTechnicalWorkMutation();
  const [startedDate, setStartedDate] = useState(today());
  const [expectedCompletionDate, setExpectedCompletionDate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!show) return;
    setStartedDate(today());
    setExpectedCompletionDate("");
    setRemarks("");
    setSelected(
      technicians.map((t) => ({
        value: t.employeeRecordGuid,
        label: `${t.employeeFirstName || ""} ${t.employeeLastName || ""}`.trim() + ` · ${t.employeeCode}`,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, work?.recordGuid]);

  const handleStart = async () => {
    if (selected.length === 0) return toast.error("Assign at least one technician to start this work");
    try {
      await startWork({
        recordGuid: work.recordGuid,
        startedDate,
        startedRemarks: remarks.trim() || null,
        expectedCompletionDate: expectedCompletionDate || null,
        technicianEmployeeRecordGuids: selected.map((s) => s.value),
      }).unwrap();
      toast.success("Work started");
      onClose();
    } catch (e) {
      toast.error(e?.message || "Could not start this work");
    }
  };

  return (
    <Modal show={show} onHide={isLoading ? undefined : onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Start Work</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          {work?.workNumber} &middot; {work?.customerName} &middot; {work?.branchCode}
        </p>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Start date</label>
            <input type="date" className="form-control" value={startedDate} onChange={(e) => setStartedDate(e.target.value)} />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Expected completion <span className="text-muted">(optional)</span></label>
            <input
              type="date"
              className="form-control"
              value={expectedCompletionDate}
              onChange={(e) => setExpectedCompletionDate(e.target.value)}
            />
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">Technician(s) <span className="text-danger">*</span></label>
          <TechnicianPicker
            branchId={work?.branchId}
            value={selected}
            onChange={(v) => setSelected(v || [])}
            menuPortalTarget={document.body}
          />
        </div>
        <div className="mb-1">
          <label className="form-label">Remarks <span className="text-muted">(optional)</span></label>
          <textarea className="form-control" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
        <Button variant="primary" onClick={handleStart} disabled={isLoading}>
          {isLoading ? "Starting..." : "Start Work"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StartWorkModal;
